import React, { useState } from 'react';
import { Button, TextField, TableRow, TableCell } from '@mui/material';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';
import MUIDataTableWrapper from 'components/MUIDataTableWrapper/MUIDataTableWrapper';
import axios from 'axios';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import Dropdown from 'components/Dropdown/Dropdown';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface ReportType {
  name: string;
  label: string;
}

interface ColumnType {
  name: string;
  label: string;
  filterType?: string;
  dataType?: string;
}

interface IProps {
  reportTypes: ReportType[];
  columns?: ColumnType[];
  data?: unknown[];
  totals?: unknown[];
}

const defaultGridDetailsState = {
  data: [] as unknown[],
  columns: [] as ColumnType[],
  options: {},
};

const ReportingGC: React.FC<IProps> = (props) => {
  const [searchFieldValues, setSearchFieldValues] = React.useState({
    startDate: '',
    endDate: '',
    currentReport: 'CventAttendeesReport',
  });

  const [gridDetails, setGridDetails] = useState(defaultGridDetailsState);

  const [requestPending, setRequestPending] = React.useState(false);

  const [needsDateRange, setNeedsDateRange] = React.useState(false);

  const onSearchFieldChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setSearchFieldValues({ ...searchFieldValues, [name]: value });
  };

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchFieldValues.currentReport != '') {
      setRequestPending(true);
      axios.post(`/api/Reporting/report`, searchFieldValues).then(
        (resp) => {
          setGridDetails({
            ...gridDetails,
            columns: resp.data.columns.map((col: ColumnType) => {
              return {
                name: col.name,
                label: col.label,
                options: {
                  filterType: col.filterType,
                },
              };
            }),
            data: resp.data.resultSet.map((row: Record<string, unknown>) => {
              _.forOwn(row, (value, key) => {
                const col = _.find(
                  resp.data.columns,
                  (col: ColumnType) => col.name === key
                );
                if (!col) return;
                if (col.dataType === 'date') {
                  const utcMoment = dayjs.utc(value as string).toDate();
                  row[key] = dayjs(utcMoment).format('MM/DD/YYYY hh:mm a');
                }
              });
              return row;
            }),
          });
          setRequestPending(false);
        },
        (error) => {
          console.log(error);
          setRequestPending(false);
        }
      );
    } else {
      setGridDetails(defaultGridDetailsState);
    }
  };

  React.useEffect(() => {
    if (
      searchFieldValues.currentReport == 'AttendeeByDateRange' ||
      searchFieldValues.currentReport == 'VisitorsByDateRange'
    ) {
      setNeedsDateRange(true);
    } else {
      setNeedsDateRange(false);
    }
  }, [searchFieldValues.currentReport]);

  return (
    <Grid container direction="column">
      <Grid size={{ xs: 12, md: 3 }}>
        <form onSubmit={onFormSubmit} method={'post'}>
          <Dropdown
            id={'report-select-dropdown'}
            name={'currentReport'}
            onChange={onSearchFieldChange}
            label={'Select a Report'}
            selectedPropsValue={searchFieldValues.currentReport}
            values={props.reportTypes.map((r, index) => {
              return (
                <option value={r.name} key={index}>
                  {r.label}
                </option>
              );
            })}
          />

          {needsDateRange && (
            <Grid
              container
              direction="row"
              alignItems="flex-start"
              className="pt-4"
              spacing={2}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  id={'audit-search-start-date'}
                  label="Start Date"
                  name="startDate"
                  onChange={onSearchFieldChange}
                  type="date"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  id={'audit-search-end-date'}
                  label="End Date"
                  onChange={onSearchFieldChange}
                  name="endDate"
                  type="date"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 3 }} className="pt-3">
            <Button
              className="btn"
              type="submit"
              fullWidth
              size="large"
              variant="contained"
              color="primary"
            >
              Submit
            </Button>
          </Grid>
        </form>
      </Grid>

      {requestPending ? (
        <GenericSpinner padding={0} includeOverlay={false} />
      ) : (
        <Grid container>
          {gridDetails.columns.length === 0 ? null : (
            <Grid
              size={{ xs: 12, md: 12 }}
              className="pt-4"
              style={{ display: 'table', tableLayout: 'fixed', width: '100%' }}
            >
              <MUIDataTableWrapper
                title={''}
                data={gridDetails.data}
                excludeMobile={true}
                options={{
                  searchOpen: true,
                  selectableRows: false,
                  downloadOptions: {
                    filterOptions: {
                      useDisplayedColumnsOnly: true,
                      useDisplayedRowsOnly: true,
                    },
                  },
                }}
                columns={gridDetails.columns}
                setExpandableRowContent={(rowData: unknown[] | unknown) => {
                  return (
                    <TableRow className="expandable-row-tr">
                      <TableCell className="tbl-col-container">
                        <div className="tbl-col-title">Object Type</div>
                        <div>{(rowData as unknown[])[2] ? String((rowData as unknown[])[2]) : 'N/A'}</div>
                      </TableCell>
                      <TableCell className="tbl-col-container">
                        <div className="tbl-col-title">Summary</div>
                        <div>{(rowData as unknown[])[3] ? String((rowData as unknown[])[3]) : 'N/A'}</div>
                      </TableCell>
                    </TableRow>
                  );
                }}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default ReportingGC;
